import { Link } from 'react-router-dom';
import { FolderKanban, CheckCircle2, Clock, Bell, ArrowRight, BellRing, BellOff, Users, Flag } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useProjects } from '@/hooks/use-projects';
import { useAuth } from '@/hooks/use-auth';
import { useUnreadNotificationCount } from '@/hooks/use-notifications';
import { useFollowedTasks, useUnfollowTask } from '@/hooks/use-tasks';
import { getInitials, getPriorityHexColor, getContrastTextColor, formatRelativeTime } from '@/lib/utils';

export function DashboardPage() {
  const { user } = useAuth();
  const { data: projects = [], isLoading } = useProjects();
  const { data: unreadNotifications = 0 } = useUnreadNotificationCount();
  const { data: followedTasks = [], isLoading: isLoadingFollowed } = useFollowedTasks();
  const unfollowTask = useUnfollowTask();

  const totalTasks = projects.reduce((acc, p) => acc + (p._count?.tasks || 0), 0);
  const isClient = user?.role === 'client';

  const getRoleLabel = (role: string | undefined) => {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'user':
        return 'Team Member';
      case 'client':
        return 'Client';
      default:
        return role;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold">
            Welcome back, {user?.displayName?.split(' ')[0]}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-muted-foreground">
              {isClient
                ? 'View your assigned tasks and project updates'
                : "Here's what's happening with your projects"}
            </p>
            <Badge variant="outline" className="capitalize">
              {getRoleLabel(user?.role)}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isClient ? 'Your Projects' : 'Total Projects'}
            </CardTitle>
            <FolderKanban className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{projects.length}</div>
            <p className="text-xs text-muted-foreground">Active projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              {isClient ? 'Your Tasks' : 'Total Tasks'}
            </CardTitle>
            <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTasks}</div>
            <p className="text-xs text-muted-foreground">Across all projects</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Due Soon</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">-</div>
            <p className="text-xs text-muted-foreground">Tasks due this week</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Notifications</CardTitle>
            <Bell className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {unreadNotifications > 0 ? unreadNotifications : '-'}
            </div>
            <p className="text-xs text-muted-foreground">
              {unreadNotifications > 0 ? 'Unread notifications' : 'All caught up'}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Projects</h2>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/projects" className="gap-1">
                View all
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : projects.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FolderKanban className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No projects yet</h3>
                <p className="text-muted-foreground text-center mb-4">
                  {isClient
                    ? 'You have not been assigned to any projects yet'
                    : 'Create your first project to get started'}
                </p>
                {!isClient && (
                  <Button asChild>
                    <Link to="/projects">Go to Projects</Link>
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {projects.slice(0, 5).map((project) => (
                <Link key={project.id} to={`/projects/${project.id}`}>
                  <Card className="hover:border-primary/50 transition-colors cursor-pointer">
                    <CardHeader className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div
                            className="h-3 w-3 rounded-sm shrink-0"
                            style={{ backgroundColor: project.color }}
                          />
                          <CardTitle className="text-sm font-medium">
                            {project.name}
                          </CardTitle>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{project._count?.tasks || 0} tasks</span>
                        </div>
                      </div>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Quick Actions</h2>
          </div>
          <Card>
            <CardContent className="pt-6 space-y-3">
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/projects">
                  <FolderKanban className="mr-2 h-4 w-4" />
                  Browse Projects
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/search">
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Search Tasks
                </Link>
              </Button>
              <Button variant="outline" className="w-full justify-start" asChild>
                <Link to="/notifications">
                  <Bell className="mr-2 h-4 w-4" />
                  View Notifications
                  {unreadNotifications > 0 && (
                    <Badge variant="destructive" className="ml-auto">
                      {unreadNotifications}
                    </Badge>
                  )}
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tickets I Follow Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <BellRing className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold">Tickets I Follow</h2>
            {followedTasks.length > 0 && (
              <Badge variant="secondary" className="ml-1">
                {followedTasks.length}
              </Badge>
            )}
          </div>
        </div>
        {isLoadingFollowed ? (
          <div className="flex items-center justify-center h-32">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : followedTasks.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BellOff className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">You are not following any tickets yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Follow tickets to stay updated on their progress without being assigned
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0 divide-y divide-border">
              {followedTasks.map((task) => {
                const priorityBg = getPriorityHexColor(task.priority);
                const priorityText = getContrastTextColor(priorityBg);
                const statusBg = task.status?.color ?? '#6b7280';
                const statusText = getContrastTextColor(statusBg);

                return (
                  <div
                    key={task.id}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors group"
                  >
                    <Link
                      to={`/tasks/${task.id}`}
                      className="flex-1 min-w-0 flex items-center gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium truncate">{task.title}</span>
                          <Badge
                            className="h-5 text-xs shrink-0"
                            style={{ backgroundColor: statusBg, color: statusText, borderColor: 'transparent' }}
                          >
                            {task.status?.name ?? 'Unknown'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {task.project && (
                            <>
                              <span>{task.project.name}</span>
                              {task.list && <span>/</span>}
                            </>
                          )}
                          {task.list && <span>{task.list.name}</span>}
                          <span>•</span>
                          <span>Updated {formatRelativeTime(task.effectiveLastUpdated ?? task.updatedAt)}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge
                          className="h-5 text-xs capitalize"
                          style={{ backgroundColor: priorityBg, color: priorityText, borderColor: 'transparent' }}
                        >
                          <Flag className="h-3 w-3 mr-1" />
                          {task.priority}
                        </Badge>
                        {task.assignees?.length > 0 && (
                          <div className="flex -space-x-1">
                            {task.assignees.slice(0, 3).map((a) => (
                              <Avatar key={a.id} className="h-6 w-6 border-2 border-background">
                                <AvatarImage src={a.avatarUrl ?? undefined} />
                                <AvatarFallback className="text-[8px]">
                                  {getInitials(a.displayName)}
                                </AvatarFallback>
                              </Avatar>
                            ))}
                            {task.assignees.length > 3 && (
                              <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-[10px] border-2 border-background">
                                +{task.assignees.length - 3}
                              </div>
                            )}
                          </div>
                        )}
                        {task.assignedTeam && (
                          <Badge variant="outline" className="h-5 text-xs gap-1">
                            <Users className="h-3 w-3" />
                            {task.assignedTeam.name}
                          </Badge>
                        )}
                      </div>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                      onClick={(e) => {
                        e.preventDefault();
                        unfollowTask.mutate(task.id);
                      }}
                      disabled={unfollowTask.isPending}
                      title="Unfollow"
                    >
                      <BellOff className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
